import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { CLIENTS } from '@/data/clients';
import { CLIENT_DAILY_ROWS } from '@/data/clientDetail';
import type { ClientDailyRow } from '@/data/clientDetail';
import type { Client } from '@/types/entities';

interface UseClientDetailResult {
  client: Client | undefined;
  dailyRows: readonly ClientDailyRow[];
  isLoading: boolean;
}

export function useClientDetail(clientId: string | undefined): UseClientDetailResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const clientQuery = useQuery({
    queryKey: ['clientDetail', orgId, clientId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{
        id: string;
        name: string;
        slug: string;
        createdAt: string;
        systemOwned: boolean;
        _count: { projects: number };
      }>(`/api/v1/orgs/${orgId}/clients/${clientId}`, token);
      // Also fetch projects for this client
      const projRes = await apiFetch<{
        projects: Array<{
          id: string;
          clientId: string;
          name: string;
          billable: boolean;
          createdAt: string;
          systemOwned: boolean;
        }>;
      }>(`/api/v1/orgs/${orgId}/projects?clientId=${clientId}`, token);

      return {
        id: res.id,
        name: res.name,
        businessUnitId: '',
        createdAt: res.createdAt,
        systemOwned: res.systemOwned,
        projects: projRes.projects
          .filter((p) => p.clientId === clientId)
          .map((p) => ({
            id: p.id,
            clientId: p.clientId as string | null,
            name: p.name,
            type: p.billable ? ('billable' as const) : ('general' as const),
            createdAt: p.createdAt,
            systemOwned: p.systemOwned,
          })),
      } satisfies Client;
    },
    enabled: !mockupMode && !!orgId && !!clientId,
  });

  const reportQuery = useQuery({
    queryKey: ['clientReport', orgId, clientId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      const res = await apiFetch<{
        client: { id: string; name: string };
        summary: { totalHours: number; billableHours: number; generalHours: number };
        people: Array<{ id: string; name: string; totalHours: number }>;
      }>(`/api/v1/orgs/${orgId}/reports/clients/${clientId}${qsStr ? `?${qsStr}` : ''}`, token);
      // The report doesn't return daily rows directly — return summary as a single row
      return [
        {
          date: dateRange.start,
          hours: res.summary.totalHours,
          billable: res.summary.billableHours,
          general: res.summary.generalHours,
          firstActivity: '',
          lastActivity: '',
        } satisfies ClientDailyRow,
      ];
    },
    enabled: !mockupMode && !!orgId && !!clientId,
  });

  if (mockupMode) {
    const client = CLIENTS.find((c) => c.id === clientId);
    return { client, dailyRows: CLIENT_DAILY_ROWS, isLoading: false };
  }

  return {
    client: clientQuery.data,
    dailyRows: reportQuery.data ?? [],
    isLoading: clientQuery.isLoading || reportQuery.isLoading,
  };
}
